/**
 * Admin (super_admin) home page.
 *
 * Before this page existed, super_admin landed on /manager/dashboard
 * — which works for the overlap (approvals, team capacity) but
 * (a) buries the admin-only surfaces (Users, Maintenance, Configs,
 * Concentration Accounts) behind a generic "Manager Dashboard"
 * label and (b) implies super_admin is just a manager with extras.
 * Splitting the landing page lets the role have its own framing
 * without losing the operational data — the manager view is still
 * one click away via the "Operational dashboard" card.
 *
 * Scope: what waits for the super_admin first (`WaitingForMe`, #340:
 * every step a manager takes, the compliance officer's, the fund accounts
 * awaiting a second confirmation and the products not operable), then the
 * quick-action card grid and a deep-link to the operational manager
 * dashboard.
 */
import { Link } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import WaitingForMe from '../../components/staff/WaitingForMe';
import { useAuthStore } from '../../stores/authStore';
import { fullName } from '../../lib/fullName';
import { colors, gradients } from '../../styles/theme';

interface CardSpec {
  readonly to: string;
  readonly title: string;
  readonly description: string;
  readonly emoji: string;
}

const ADMIN_CARDS: ReadonlyArray<CardSpec> = [
  {
    to: '/admin/staff',
    title: 'Staff',
    description: 'Internal staff accounts: manage roles, reset MFA, deactivate.',
    emoji: '👥',
  },
  {
    to: '/admin/clients',
    title: 'Clients',
    description: 'Client accounts: security ops (lock/unlock, password, sessions, TOTP).',
    emoji: '🧑‍💼',
  },
  {
    to: '/admin/maintenance',
    title: 'Maintenance windows',
    description: 'Schedule, cancel, and surface system maintenance banners.',
    emoji: '🛠️',
  },
  {
    to: '/admin/commitment-configs',
    title: 'Commitment configs',
    description: 'Versioned commitment terms per product (cadence, caps, fees).',
    emoji: '📜',
  },
  {
    to: '/admin/concentration-accounts',
    title: 'Concentration accounts',
    description: 'Master deposit accounts surfaced on /client/fund.',
    emoji: '🏦',
  },
  {
    to: '/admin/email-templates',
    title: 'Email templates',
    description: 'Edit transactional email copy. Preview before going live.',
    emoji: '📧',
  },
  {
    to: '/manager/leads',
    title: 'Leads',
    description: 'Review access-request submissions; approve to send an invite.',
    emoji: '📨',
  },
  {
    to: '/manager/invitations',
    title: 'Invitations',
    description: 'Bulk-invite imported leads. Track which acquired which.',
    emoji: '🎟️',
  },
];

export default function AdminDashboard(): JSX.Element {
  const { user } = useAuthStore();
  const displayName = fullName(user) || user?.email || 'Admin';

  return (
    <>
      <PageHeader
        title="Admin"
        subtitle={
          <span>
            Welcome back, {displayName}
            <span
              style={{
                marginLeft: 12,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 11,
                color: colors.muted,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: colors.gold,
                }}
              />{' '}
              Super admin
            </span>
          </span>
        }
      />

      <WaitingForMe />

      {/* Quick actions */}
      <section className="mb-8">
        <h2
          className="text-xs font-semibold uppercase tracking-wide mb-3"
          style={{ color: colors.muted }}
        >
          Admin actions
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 16,
          }}
        >
          {ADMIN_CARDS.map((card) => (
            <Link
              key={card.to}
              to={card.to}
              className="rounded-2xl p-5 transition-transform"
              style={{
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                color: 'inherit',
                textDecoration: 'none',
                display: 'block',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.gold;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.border;
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <span style={{ fontSize: 22 }} aria-hidden>{card.emoji}</span>
                <span
                  className="text-base font-semibold"
                  style={{ color: colors.ink }}
                >
                  {card.title}
                </span>
              </div>
              <p className="text-sm" style={{ color: colors.muted, lineHeight: 1.5 }}>
                {card.description}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* Cross-link to the manager dashboard — super_admin still needs
          easy access to approvals + team capacity. Single card so the
          two surfaces are clearly distinct from above. */}
      <section>
        <h2
          className="text-xs font-semibold uppercase tracking-wide mb-3"
          style={{ color: colors.muted }}
        >
          Operations
        </h2>
        <Link
          to="/manager/dashboard"
          className="rounded-2xl p-5 transition-transform"
          style={{
            background: gradients.navy,
            color: '#FFFFFF',
            textDecoration: 'none',
            display: 'block',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <span style={{ fontSize: 22 }} aria-hidden>📊</span>
            <span className="text-base font-semibold">Operational dashboard</span>
          </div>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
            Manager workspace — approvals queue, team capacity, SLA + compliance
            counters, deposits by status. Same surface managers see.
          </p>
        </Link>
      </section>
    </>
  );
}
